import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Card, Typography, Tag, Input, Select,
  Row, Col, Statistic, Progress, Radio, Alert, Divider, message
} from 'antd';
import {
  ArrowLeftOutlined, SearchOutlined, FilterOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  DownloadOutlined, FileTextOutlined
} from '@ant-design/icons';
import { IoDocumentLock } from "react-icons/io5";
import { ResetIcon, RefreshIcon } from '../../components/common/ActionIcons';
import { BsBuildingFill } from "react-icons/bs";
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PageWrapper from '../../components/common/PageWrapper';

import { listBusinessOrganisations } from '../../actions/businessOrganisationActions';
import axios from 'axios';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const { Text } = Typography;

const BusinessOrganisationDataManagementScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState({
    organisationListFilter: {
      query: '',
      page: 1,
      limit: 10,
      filter: 'name',
    },
    searchText: '',
    searchProp: 'name',
    dsaFilter: 'all',
    dpaFilter: 'all', 
    tocFilter: 'all',
    selectedRowKeys: [],
  });

  const businessOrganisationList = useSelector(state => state.businessOrganisationList);
  const { loading, error, businessOrganisations = [], currentPage, totalPages, totalCount } = businessOrganisationList;
  const userLogin = useSelector(state => state.userLogin);

  useEffect(() => {
    dispatch(listBusinessOrganisations(state.organisationListFilter));
  }, [dispatch, state.organisationListFilter]);

  const handleRefresh = useCallback(() => {
    dispatch(listBusinessOrganisations(state.organisationListFilter));
  }, [dispatch, state.organisationListFilter]);

  const handleSearch = (value) => {
    setState(prev => ({
      ...prev,
      searchText: value,
      organisationListFilter: {
        ...prev.organisationListFilter,
        query: value,
        page: 1
      }
    }));
  };

  const handleSearchPropChange = (value) => {
    setState(prev => ({
      ...prev,
      searchProp: value,
      organisationListFilter: {
        ...prev.organisationListFilter,
        filter: value,
        query: prev.searchText
      }
    }));
  };

  const handleFilterChange = (pagination, filters, sorter) => {
    setState(prev => ({
      ...prev,
      organisationListFilter: {
        ...prev.organisationListFilter,
        page: pagination.current,
        limit: pagination.pageSize,
      }
    }));
  };

  const handleDSAFilterChange = (value) => {
    setState(prev => ({
      ...prev,
      dsaFilter: value,
      dpaFilter: 'all',
      tocFilter: 'all',
      organisationListFilter: {
        ...prev.organisationListFilter,
        page: 1,
        ...(value === 'all' ? { filter: 'name', query: prev.searchText } : {
          filter: 'dsaStatus',
          query: value === 'signed' ? 'Signed' : value === 'pending' ? 'Pending' : value === 'notSigned' ? 'NotSigned' : ''
        })
      }
    }));
  };

  const handleDPAFilterChange = (value) => {
    setState(prev => ({
      ...prev,
      dsaFilter: 'all',
      dpaFilter: value,
      tocFilter: 'all',
      organisationListFilter: {
        ...prev.organisationListFilter,
        page: 1,
        ...(value === 'all' ? { filter: 'name', query: prev.searchText } : {
          filter: 'dpaStatus',
          query: value === 'signed' ? 'Signed' : value === 'pending' ? 'Pending' : value === 'notSigned' ? 'NotSigned' : ''
        })
      }
    }));
  };

  const handleTOCFilterChange = (value) => {
    setState(prev => ({
      ...prev,
      dsaFilter: 'all',
      dpaFilter: 'all',
      tocFilter: value,
      organisationListFilter: {
        ...prev.organisationListFilter,
        page: 1,
        ...(value === 'all' ? { filter: 'name', query: prev.searchText } : {
          filter: 'tocStatus',
          query: value === 'signed' ? 'Signed' : value === 'pending' ? 'Pending' : value === 'notSigned' ? 'NotSigned' : ''
        })
      }
    }));
  };

  const handleReset = () => {
    setState(prev => ({
      ...prev,
      searchText: '',
      searchProp: 'name',
      dsaFilter: 'all',
      dpaFilter: 'all',
      tocFilter: 'all',
      organisationListFilter: {
        query: '',
        page: 1,
        limit: 10,
        filter: 'name',
      }
    }));
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!businessOrganisations?.length) return { total: 0, dsaSigned: 0, dpaSigned: 0, tocSigned: 0 };
    
    return {
      total: businessOrganisations.length,
      dsaSigned: businessOrganisations.filter(org => org.dsaStatus === 'Signed').length,
      dpaSigned: businessOrganisations.filter(org => org.dpaStatus === 'Signed').length,
      tocSigned: businessOrganisations.filter(org => org.tocStatus === 'Signed').length,
    };
  }, [businessOrganisations]);

  const filteredOrganisations = businessOrganisations || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Signed': return 'green';
      case 'Pending': return 'orange';
      case 'InProgress': return 'blue';
      case 'NotSigned': return 'red';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Signed': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'Pending': return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'InProgress': return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'NotSigned': return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      default: return <CloseCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // PDF Download function
  const downloadAgreementPDF = async (agreementType, organisation) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const generatedDate = new Date().toLocaleDateString();
      
      // Get the config template from the printer service
      const configResponse = await axios.get(`/print/configs/${agreementType}_agreement`, {
        headers: {
          Authorization: `Bearer ${userLogin.userInfo.token}`
        }
      });

      // Prepare data based on agreement type using organisation details
      let templateData = {
        businessOrganisationIdentifier: organisation.identifier,
        effectiveDate: currentDate,
        generatedDate: generatedDate
      };
      
      // Add signature data based on agreement type
      if (agreementType === 'dsa') {
        templateData.controllerSignedBy = organisation.dsaSignedBy || 'Pending';
        templateData.controllerSignatureDate = organisation.dsaSignedDate || currentDate;
      } else if (agreementType === 'dpa') {
        templateData.controllerSignedBy = organisation.dpaSignedBy || 'Pending';
        templateData.controllerSignatureDate = organisation.dpaSignedDate || currentDate;
      } else if (agreementType === 'toc') {
        templateData.controllerSignedBy = organisation.tocSignedBy || 'Pending';
        templateData.controllerSignatureDate = organisation.tocSignedDate || currentDate;
      }

      // Merge the template config with our data
      const printRequest = {
        ...configResponse.data.content,
        data: {
          ...configResponse.data.content.data,
          ...templateData
        }
      };

      // Generate the PDF
      const pdfResponse = await axios.post('/print/documents/generate', printRequest, {
        headers: {
          Authorization: `Bearer ${userLogin.userInfo.token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${agreementType.toUpperCase()}_Agreement_${organisation.name}_${currentDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(t('PDF downloaded successfully'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error(t('Failed to generate PDF agreement'));
    }
  };

  const columns = [
    {
      title: t('Organisation'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
            {text}
          </div>
          <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {record.identifier}
          </div>
          <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {record.type}
          </div>
        </div>
      ),
    },
    {
      title: t('Contact'),
      key: 'contact',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {record.contactEmail}
          </div>
          <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {record.contactPhone}
          </div>
        </div>
      ),
    },
    {
      title: t('DSA Status'),
      dataIndex: 'dsaStatus',
      key: 'dsaStatus',
      width: 150,
      filters: [
        { text: t('Signed'), value: 'Signed' },
        { text: t('Pending'), value: 'Pending' },
        { text: t('Not Signed'), value: 'NotSigned' },
        { text: t('In Progress'), value: 'InProgress' },
      ],
      onFilter: (value, record) => record.dsaStatus === value,
      render: (status, record) => (
        <Space>
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)} size="small">
            {status}
          </Tag>
          {status === 'Signed' && (
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadAgreementPDF('dsa', record)}
              style={{ padding: '2px 4px', height: 'auto' }}
              title={t('Download DSA PDF')}
            />
          )}
        </Space>
      ),
    },
    {
      title: t('DPA Status'),
      dataIndex: 'dpaStatus',
      key: 'dpaStatus',
      width: 150,
      filters: [
        { text: t('Signed'), value: 'Signed' },
        { text: t('Pending'), value: 'Pending' },
        { text: t('Not Signed'), value: 'NotSigned' },
        { text: t('In Progress'), value: 'InProgress' },
      ],
      onFilter: (value, record) => record.dpaStatus === value,
      render: (status, record) => (
        <Space>
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)} size="small">
            {status}
          </Tag>
          {status === 'Signed' && (
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadAgreementPDF('dpa', record)}
              style={{ padding: '2px 4px', height: 'auto' }}
              title={t('Download DPA PDF')}
            />
          )}
        </Space>
      ),
    },
    {
      title: t('TOC Status'),
      dataIndex: 'tocStatus',
      key: 'tocStatus',
      width: 150,
      filters: [
        { text: t('Signed'), value: 'Signed' },
        { text: t('Pending'), value: 'Pending' },
        { text: t('Not Signed'), value: 'NotSigned' },
        { text: t('In Progress'), value: 'InProgress' },
      ],
      onFilter: (value, record) => record.tocStatus === value,
      render: (status, record) => (
        <Space>
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)} size="small">
            {status}
          </Tag>
          {status === 'Signed' && (
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadAgreementPDF('toc', record)}
              style={{ padding: '2px 4px', height: 'auto' }}
              title={t('Download TOC PDF')}
            />
          )}
        </Space>
      ),
    },
    {
      title: t('Agreement Progress'),
      key: 'progress',
      width: 150,
      render: (_, record) => {
        const signed = [record.dsaStatus, record.dpaStatus, record.tocStatus].filter(s => s === 'Signed').length;
        const progress = (signed / 3) * 100;
        return (
          <div>
            <Progress 
              percent={progress} 
              size="small" 
              status={progress === 100 ? 'success' : 'active'}
              format={(percent) => `${signed}/3`}
            />
            <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
              {signed} of 3 signed
            </Text>
          </div>
        );
      },
    },
    {
      title: t('Last Updated'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
      render: (date) => (
        <Text style={{ fontSize: '11px' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
  ];

  const searchOptions = [
    { value: 'name', label: t('Name') },
    { value: 'identifier', label: t('Identifier') },
    { value: 'type', label: t('Type') },
    { value: 'contactEmail', label: t('Contact Email') },
  ];

  return (
    <PageWrapper
      title={t('Data Sharing Agreement Compliance Dashboard')}
      icon={<FileTextOutlined style={{ fontSize: 24 }} />}
      showCard={false}
    >
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t('Total Organisations')}
              value={stats.total}
              prefix={<BsBuildingFill />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t('DSA Signed')}
              value={stats.dsaSigned}
              suffix={`/ ${stats.total}`}
              prefix={<IoDocumentLock />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t('DPA Signed')}
              value={stats.dpaSigned}
              suffix={`/ ${stats.total}`}
              prefix={<IoDocumentLock />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title={t('TOC Signed')}
              value={stats.tocSigned}
              suffix={`/ ${stats.total}`}
              prefix={<IoDocumentLock />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <ApiErrorAlert error={error} closable />

        {/* Search and Filter Controls */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={4} lg={3}>
              <Text strong>
                <FilterOutlined /> {t("Filter")}:
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <Select
                placeholder={t("Search by")}
                style={{ width: '100%' }}
                value={state.searchProp}
                onChange={handleSearchPropChange}
              >
                {searchOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={10}>
              <Input.Search
                placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === state.searchProp)?.label || ''}`}
                onSearch={handleSearch}
                value={state.searchText}
                onChange={(e) => setState(prev => ({ ...prev, searchText: e.target.value }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={24} md={6} lg={6} style={{ textAlign: 'right' }}>
              <Space>
                <Button icon={<ResetIcon />} onClick={handleReset} size="small">
                  {t("Reset all filters")}
                </Button>
                <Button
                  icon={<RefreshIcon />}
                  onClick={handleRefresh}
                  size="small"
                >
                  {t("Refresh")}
                </Button>
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          <Row gutter={[16, 8]}>
            <Col xs={24} sm={8}>
              <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>DSA:</Text>
              <Radio.Group
                value={state.dsaFilter}
                onChange={(e) => handleDSAFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="signed">Signed</Radio.Button>
                <Radio.Button value="pending">Pending</Radio.Button>
                <Radio.Button value="notSigned">Not Signed</Radio.Button>
              </Radio.Group>
            </Col>
            <Col xs={24} sm={8}>
              <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>DPA:</Text>
              <Radio.Group
                value={state.dpaFilter}
                onChange={(e) => handleDPAFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="signed">Signed</Radio.Button>
                <Radio.Button value="pending">Pending</Radio.Button>
                <Radio.Button value="notSigned">Not Signed</Radio.Button>
              </Radio.Group>
            </Col>
            <Col xs={24} sm={8}>
              <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>TOC:</Text>
              <Radio.Group
                value={state.tocFilter}
                onChange={(e) => handleTOCFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="signed">Signed</Radio.Button>
                <Radio.Button value="pending">Pending</Radio.Button>
                <Radio.Button value="notSigned">Not Signed</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredOrganisations}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: state.organisationListFilter.page,
            pageSize: state.organisationListFilter.limit,
            total: totalCount ?? (totalPages != null ? totalPages * state.organisationListFilter.limit : undefined),
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
          }}
          onChange={handleFilterChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </PageWrapper>
  );
};

export default BusinessOrganisationDataManagementScreen;
